/**
 * ONDE escrever num documento já preenchido — a decisão, sem Office.js.
 *
 * As ferramentas de inserção ancoravam num PARÁGRAFO: "depois do tópico 4" era
 * "logo abaixo da linha do título 4". Isso é certo para "no começo do tópico" e
 * errado para "no fim do tópico": o QA viu um novo tópico inserido entre o título
 * do último tópico e o conteúdo dele (o antigo ficou vazio e o novo "roubou" o
 * texto), e imagens pedidas "no final do tópico" irem para o início. Aqui mora a
 * noção de SEÇÃO — do título até o próximo título de nível igual ou maior — e as
 * regras que evitam os defeitos clássicos em volta de tabela:
 *
 *  - parágrafo de TABELA não é lugar de inserção: "depois de" um trecho que está
 *    numa célula é depois da TABELA (inserir depois da célula escreve DENTRO dela);
 *  - o parágrafo vazio logo depois de uma tabela é EXIGIDO pela norma
 *    (`empty_paragraphs_after_table`): nunca é reaproveitado para escrever, e
 *    quem insere depois de uma tabela sem esse vazio cria o vazio antes.
 *
 * Testado por `npm run check:sections`. Quem executa o plano é `tools.ts`.
 */
/** Título que conta como fronteira de seção (fora de tabela). */
function levelOf(p) {
    return p.inTable ? null : p.level;
}
/**
 * Último parágrafo da seção aberta em `startIdx`: tudo até o próximo título de
 * nível MENOR OU IGUAL a `level` (um heading_2 não fecha a seção de um
 * heading_1; outro heading_1 fecha). Sem próximo título, vai até o fim.
 */
export function sectionEndIndex(paras, startIdx, level) {
    for (let i = startIdx + 1; i < paras.length; i++) {
        const lv = levelOf(paras[i]);
        if (lv !== null && lv <= level)
            return i - 1;
    }
    return paras.length - 1;
}
/**
 * O título que abre a seção onde está `idx` (o próprio, se for título). `null`
 * quando não há título antes — o documento não tem seções até ali.
 */
export function enclosingHeadingIndex(paras, idx) {
    for (let i = idx; i >= 0; i--) {
        const lv = levelOf(paras[i]);
        if (lv !== null && lv >= 1)
            return { index: i, level: lv };
    }
    return null;
}
/** O vazio em `i` é o EXIGIDO depois de uma tabela? */
export function isReservedAfterTable(paras, i) {
    return i > 0 && !!paras[i] && paras[i].empty && !paras[i].inTable && paras[i - 1].inTable;
}
function tableEndFrom(paras, idx) {
    let t = idx;
    while (t + 1 < paras.length && paras[t + 1].inTable)
        t++;
    return t;
}
function tableStartFrom(paras, idx) {
    let s = idx;
    while (s - 1 >= 0 && paras[s - 1].inTable)
        s--;
    return s;
}
function afterTablePlan(paras, tableEnd) {
    const seguinte = tableEnd + 1;
    const vazio = seguinte < paras.length && paras[seguinte].empty && !paras[seguinte].inTable;
    return { kind: 'afterTable', tableEnd, blankIndex: vazio ? seguinte : null };
}
/** "Depois de" o parágrafo `anchorIdx`. */
export function planAfter(paras, anchorIdx) {
    if (paras[anchorIdx].inTable)
        return afterTablePlan(paras, tableEndFrom(paras, anchorIdx));
    const prox = anchorIdx + 1;
    if (prox < paras.length && paras[prox].empty && !paras[prox].inTable) {
        return { kind: 'reuse', index: prox };
    }
    return { kind: 'after', index: anchorIdx };
}
/** "Antes de" o parágrafo `anchorIdx`. */
export function planBefore(paras, anchorIdx) {
    if (paras[anchorIdx].inTable) {
        return { kind: 'beforeTable', tableStart: tableStartFrom(paras, anchorIdx) };
    }
    const ant = anchorIdx - 1;
    if (ant >= 0 && paras[ant].empty && !paras[ant].inTable && !isReservedAfterTable(paras, ant)) {
        return { kind: 'reuse', index: ant };
    }
    return { kind: 'before', index: anchorIdx };
}
/**
 * No FIM da seção de nível `level` aberta em `anchorIdx`: depois do último
 * parágrafo dela — ou dentro dele, se for uma sobra vazia; ou depois da tabela
 * que a encerra, respeitando o vazio exigido.
 */
export function planEndOfSection(paras, anchorIdx, level) {
    const fim = sectionEndIndex(paras, anchorIdx, level);
    if (fim === anchorIdx)
        return { kind: 'after', index: anchorIdx }; // seção sem conteúdo
    if (paras[fim].inTable)
        return afterTablePlan(paras, fim);
    if (paras[fim].empty) {
        // O vazio exigido depois de tabela fica; escreve depois dele.
        if (isReservedAfterTable(paras, fim))
            return { kind: 'after', index: fim };
        return { kind: 'reuse', index: fim };
    }
    return { kind: 'after', index: fim };
}
/**
 * Índices dos parágrafos logo depois de uma tabela que NÃO têm os `keep` vazios
 * exigidos — onde a faxina insere o que falta. Começa em `from` (fim do
 * prólogo: a capa não é mexida). O último parágrafo do corpo conta como vazio
 * (é o que o template reserva).
 */
export function missingBlankAfterTables(paras, keep, from = 0) {
    if (keep <= 0)
        return [];
    const faltando = [];
    for (let i = Math.max(1, from); i < paras.length; i++) {
        if (paras[i].inTable || !paras[i - 1].inTable)
            continue;
        let vazios = 0;
        while (i + vazios < paras.length && paras[i + vazios].empty && !paras[i + vazios].inTable) {
            vazios++;
        }
        if (vazios < keep)
            faltando.push(i);
    }
    return faltando;
}
